<script setup>
import { useI18n } from 'vue-i18n'

const props = defineProps({
  sidebarExpanded: {
    type: Boolean,
    required: true,
  },
  footerMenuOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:footerMenuOpen',
  'footerMenuOpen',
  'openSettings',
  'openUserManual',
])

const { t } = useI18n()

function toggleFooterMoreMenu() {
  emit('footerMenuOpen')
  emit('update:footerMenuOpen', !props.footerMenuOpen)
}

function openSettingsFromFooter() {
  emit('update:footerMenuOpen', false)
  emit('openSettings')
}

function openFaqFromFooter() {
  emit('update:footerMenuOpen', false)
  emit('openUserManual')
}
</script>

<template>
  <div
    class="sidebar-footer"
    :class="{ 'sidebar-footer--icon-only': !sidebarExpanded }"
  >
    <div class="menu-divider" />
    <div class="sidebar-footer-settings-row">
      <button
        type="button"
        class="sidebar-footer-settings-trigger"
        :class="{ 'sidebar-footer-settings-trigger--open': footerMenuOpen }"
        :aria-expanded="footerMenuOpen"
        aria-haspopup="menu"
        :aria-label="t('newBradley.sidebar.settingsAndFaq')"
        @click.stop="toggleFooterMoreMenu"
      >
        <i
          class="pi pi-cog"
          aria-hidden="true"
        />
        <span
          v-if="sidebarExpanded"
          class="sidebar-footer-label"
        >{{ t('newBradley.sidebar.settingsAndFaq') }}</span>
      </button>
      <div
        v-if="footerMenuOpen"
        class="footer-dropdown-menu"
        :class="{ 'footer-dropdown-menu--collapsed': !sidebarExpanded }"
        role="menu"
        @click.stop
      >
        <button
          type="button"
          role="menuitem"
          @click="openSettingsFromFooter"
        >
          <i class="pi pi-cog" />
          <span>{{ t('newBradley.sidebar.settings') }}</span>
        </button>
        <button
          type="button"
          role="menuitem"
          @click="openFaqFromFooter"
        >
          <i class="pi pi-question-circle" />
          <span>{{ t('newBradley.sidebar.userManual') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sidebar-footer {
  flex-shrink: 0;
  margin-top: auto;
  padding-bottom: 0.25rem;
  background: #fafafe;
}

.menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0.5rem 0;
}

.sidebar-footer-settings-row {
  position: relative;
  margin: 0 0.35rem;
  padding: 0.35rem 0.25rem;
  min-height: 2.5rem;
}

.sidebar-footer-settings-trigger {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 0.75rem;
  padding: 0.35rem 0.5rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: #374151;
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.sidebar-footer-settings-trigger:hover {
  background: #f3f4f6;
}

.sidebar-footer-settings-trigger--open {
  background: #eef0f7;
}

.sidebar-footer-settings-trigger i {
  flex-shrink: 0;
  width: 20px;
  text-align: center;
  font-size: 1.125rem;
  color: var(--color-dss-primary-500);
}

.sidebar-footer-label {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.005em;
  white-space: nowrap;
  color: #374151;
}

.footer-dropdown-menu {
  position: absolute;
  z-index: 5;
  top: auto;
  right: 0.25rem;
  bottom: calc(100% + 0.125rem);
  left: 0.25rem;
  width: auto;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #ffffff;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12);
}

.footer-dropdown-menu button {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: transparent;
  padding: 0.625rem 0.75rem;
  color: #374151;
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
}

.footer-dropdown-menu button:hover {
  background: #f3f4f6;
}

.footer-dropdown-menu button i {
  color: var(--color-dss-primary-500);
}

.sidebar-footer--icon-only .sidebar-footer-settings-row {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.sidebar-footer--icon-only .sidebar-footer-settings-trigger {
  justify-content: center;
  padding: 0.75rem 0.25rem;
}

.footer-dropdown-menu--collapsed {
  z-index: 30;
  top: auto;
  right: calc(100% + 0.5rem);
  left: auto;
  bottom: 0;
  width: max-content;
  min-width: 11.5rem;
}
</style>
